import { Fragment, useState } from 'react';
import { Box, Typography, Modal, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { z } from 'zod';

import { useToaster } from '../../../../providers/ToasterProvider';

import RadiusButton from '../../../../components/RadiusButton';

import DOWNLOAD_BLUE from '../../../../assets/images/download-blue.svg';
import UPLOAD_GREY from '../../../../assets/images/upload-grey.svg';
import UPLOAD_WHITE from '../../../../assets/images/upload-white.svg';
import DELETE_ICON from '../../../../assets/images/action-delete.svg';
import mimes from '../../../../constant/mimes';
import { updateProcessingCandidate } from '../../../../services/processingCandidateService';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

const columns = [
    { id: 'index', label: 'Row Number', minWidth: 140 },
    { id: 'rollNo', label: 'Roll No', minWidth: 140 },
    { id: 'userName', label: 'User Name', minWidth: 140 },
    { id: 'errors', label: 'Errors', minWidth: 190 },
];

const updateSchema = z.object({
    rollNos: z.string().min(1, { message: 'Roll nos is required.' }),
});

export const UpdateCandidateModal = ({ projectMasterId, updateCandidateModalVisible, setUpdateCandidateModalVisible }) => {

    const [candidateFile, setCandidateFile] = useState(null);
    const [updateResult, setUpdateResult] = useState({
        isProcessed: false,
        createdRecordsCount: 0,
        errors: []
    });
    const { addNotification } = useToaster();
    const [isUpdating, setIsUpdating] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        reset,
    } = useForm({
        resolver: zodResolver(updateSchema),
    });

    const handleFileChange = ({ target }) => {
        const file = target?.files[0];
        if (file && file?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            setCandidateFile(file);
            setUpdateResult({
                isProcessed: false,
                errors: [],
                createdCount: 0,
            });
        }
    }
    const handleFileRemove = () => {
        const element = document.getElementById('candidateFile');
        if (element) {
            element.value = '';
            setCandidateFile(null);
        }
    }
    const handleModalClose = () => {
        setUpdateCandidateModalVisible(false);
        setCandidateFile(null);
        reset();
        setUpdateResult({
            isProcessed: false,
            createdCount: 0,
            errors: [],
        })
        const candidateFileEle = document.getElementById('candidateFile');
        if (candidateFileEle) {
            candidateFileEle.value = '';
        }
    }
    const handleUpdateCandidate = async (value) => {
        if (!value.rollNos || !candidateFile || !projectMasterId) {
            trigger('rollNos');
            return;
        }
        setIsUpdating(true);
        try {
            const body = new FormData();
            body.append('projectMasterId', projectMasterId);
            body.append('rollNos', JSON.stringify(value.rollNos.split(',').map(rollNo => rollNo.trim())));
            body.append('file', candidateFile);
            const response = await updateProcessingCandidate(body);
            if (response.code === 'OK') {
                if (response.data?.errors?.length) {
                    setUpdateResult({
                        isProcessed: true,
                        createdCount: response.data.createdCount,
                        errors: response.data.errors,
                    });
                } else if (response.data?.createdCount === 0) {
                    addNotification('Unexpected server error occurred.', 'danger');
                } else {
                    addNotification('Candidates added successfully.', 'success');
                    handleModalClose();
                }
            }
        } catch (error) {
            const code = error?.response?.data?.code;
            if (code === 'E_EXAM_ALREADY_ENABLED') {
                addNotification('Exam enabled for selected master.', 'danger');
            } else {
                addNotification('Unexpected error occurred.', 'danger');
            }
            console.log('There is an error while uploading bulk users', error);
        } finally {
            setIsUpdating(false);
        }
    }


    const renderTableCell = (columnId, rowIndex) => {
        const row = updateResult.errors[rowIndex];
        switch (columnId) {
            case 'index':
                return row['index']
            case 'rollNo':
                return row['rollNo']
            case 'userName':
                return row['userName']
            case 'errors':
                return <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                }}>
                    {row['errors'].map((err, index) => {
                        return <Typography key={index}>
                            {err}
                        </Typography>
                    })}

                </Box>
            default:
                return row[columnId];
        }
    }

    return (
        <Fragment>
            <Modal
                open={updateCandidateModalVisible}
                onClose={handleModalClose}
                hideBackdrop={true}
                className='backdrop'
            >
                <Box className='modal-container' sx={{
                    maxWidth: 900,
                    p: 3
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 3,
                        gap: 1,
                    }}>
                        <Typography variant="h6" component="h6" sx={{
                            color: 'primary.main'
                        }}>
                            Update Candidates
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }} component='form' autoComplete='off' onSubmit={handleSubmit(handleUpdateCandidate)}>
                        <Box sx={{
                            overflow: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2
                        }}>
                            <Typography variant='subtitle1'>
                                Roll No(s):
                            </Typography>
                            <TextField
                                size='small'
                                variant="outlined"
                                placeholder='101, 102...'
                                {...register('rollNos')}
                                error={Boolean(errors.rollNos)}
                                helperText={errors.rollNos?.message}
                                onBlur={() => trigger('rollNos')}
                                sx={{
                                    width: 320,
                                    bgcolor: 'background.base'
                                }}
                            />
                        </Box>
                        <Box sx={{
                            overflow: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pb: 1
                        }}>
                            <RadiusButton
                                variant='contained'
                                component='label'
                                htmlFor='candidateFile'
                                color='secondary'
                                sx={{ flexShrink: 0 }}
                            >
                                Select File
                            </RadiusButton>
                            <input
                                style={{ display: 'none' }}
                                type='file'
                                id='candidateFile'
                                onChange={handleFileChange}
                                accept='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                            />
                            {
                                candidateFile && candidateFile?.name && <Fragment>
                                    <Typography variant='subtitle1' >{candidateFile.name}</Typography>
                                    <img
                                        style={{ cursor: 'pointer' }}
                                        src={DELETE_ICON}
                                        onClick={handleFileRemove}
                                    />
                                </Fragment>
                            }
                        </Box>

                        {updateResult.isProcessed && <>
                            <Typography sx={{ fontSize: 17, mb: 2 }}>Successfully updated records count: {updateResult.updatedCount}</Typography>
                            {updateResult.errors?.length ? <>
                                <Typography sx={{ fontSize: 17, mb: 1 }}>Errors: </Typography>
                                <Paper sx={{
                                    width: '100%',
                                    overflow: 'hidden',
                                    bgcolor: 'background.base',
                                    maxHeight: 250,
                                    borderRadius: 1,
                                    mb: 4
                                }}>
                                    <TableContainer sx={{
                                        px: 1,
                                        pb: 1,
                                        maxHeight: 'inherit',
                                        maxWidth: 'inherit',
                                        border: '1px solid',
                                        borderColor: 'primary.main',
                                        borderRadius: 1
                                    }}>
                                        <Table stickyHeader aria-label="sticky table">
                                            <TableHead>
                                                <TableRow>
                                                    {columns.map((column) => (
                                                        <TableCell
                                                            sx={{
                                                                color: 'primary.main',
                                                                p: 1,
                                                            }}
                                                            key={column.id}
                                                            align='center'
                                                            style={{ minWidth: column.minWidth }}
                                                        >
                                                            {column.label}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {!updateResult?.errors?.length
                                                    ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                                        <TableCell colSpan={7} sx={{ border: 'none', textAlign: 'center' }}>
                                                            No records available
                                                        </TableCell>
                                                    </TableRow>
                                                    : updateResult.errors.map((row, rowIndex) => {
                                                        return (
                                                            <TableRow hover={false} role="checkbox" key={rowIndex}>
                                                                {columns.map((column) => {
                                                                    return <TableCell key={column.id} align='center' sx={{
                                                                        px: 1.5,
                                                                        py: 0.75
                                                                    }}>
                                                                        {renderTableCell(column.id, rowIndex)}
                                                                    </TableCell>
                                                                })}
                                                            </TableRow>
                                                        );
                                                    })
                                                }
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </> : null}
                        </>}


                        <Box className='actions-container'>
                            <RadiusButton
                                variant='contained'
                                color='grey'
                                sx={{ ml: 'auto' }}
                                onClick={handleModalClose}
                                disabled={isUpdating}
                            >
                                Cancel
                            </RadiusButton>
                            <LoadingButton
                                sx={{
                                    borderRadius: '46px',
                                    textTransform: 'none'
                                }}
                                variant='contained'
                                type='submit'
                                disabled={isUpdating || !candidateFile}
                                loading={isUpdating}
                                loadingPosition="start"
                                startIcon={(isUpdating || !candidateFile) ? <img src={UPLOAD_GREY} alt='' /> : <img src={UPLOAD_WHITE} alt='' />}
                            >
                                Upload
                            </LoadingButton>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </Fragment>
    );
}
